import { memo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { IconMap, ConsentLabelMap } from "../blockTypes";
import { deleteConsent } from "../../../../Redux/actions/consentAction";
import { setConsentModalDetails } from "../../../../Redux/reducers/consentSlice";
import { Edit, ContentCopy, Delete } from "@mui/icons-material";
import { copyBlockMiddleware } from "../ConsentProperties/utils";
import DeleteConfirmModal from "./DeleteConfirm";
import { BlockItemStyle, BlockDescriptorStyle } from "../block.style";

type Props = {
  block: any;
  index: number;
};

const ConsentItemWrapper: React.FC<Props> = ({ block, index }) => {
  const dispatch = useAppDispatch();

  const editable = useAppSelector((state) => state.consent.editable);
  const formSubmitting = useAppSelector(
    (state) => state.consent.formSubmitting
  );

  const Icon = IconMap[block.type];

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleCopyQuestion = () => {
    const modifiedBlock = copyBlockMiddleware(block);
    dispatch(
      setConsentModalDetails({
        block: modifiedBlock,
        show: true,
        modalIsEdit: false,
      })
    );
  };

  const handleEditQuestion = () => {
    dispatch(
      setConsentModalDetails({
        block,
        show: true,
        modalIsEdit: true,
      })
    );
  };

  const handleDeleteQuestion = () => {
    dispatch(deleteConsent(block.id));
  };

  const handleDeleteQuestionPopup = async () => {
    if (!formSubmitting) {
      setShowDeleteModal(true);
    }
  };

  return (
    <Stack sx={BlockItemStyle} justifyContent={"center"} gap={2}>
      <Stack
        direction="row"
        alignItems={"flex-start"}
        justifyContent={"space-between"}
      >
        <Stack direction="row" alignItems={"center"}>
          <Stack
            sx={BlockDescriptorStyle}
            direction="row"
            alignItems={"center"}
            justifyContent={"space-between"}
            flexShrink={0}
          >
            {Icon && <Icon />}
          </Stack>

          <Box sx={{ flex: 1, minWidth: "0px" }}>
            {block.type === "text" ? (
              <div className="tiptap-text">
                <div
                  dangerouslySetInnerHTML={{ __html: block?.label }}
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                ></div>
              </div>
            ) : (
              <Typography
                variant="body1"
                fontWeight={"medium"}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {block?.label || ConsentLabelMap?.[block.type]}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" alignItems={"center"}>
          <Tooltip title={editable ? "Edit" : "View Details"}>
            <IconButton onClick={handleEditQuestion}>
              <Edit fontSize="small" sx={{ color: "text.secondary" }} />
            </IconButton>
          </Tooltip>

          {editable && (
            <Tooltip title={"Duplicate"}>
              <IconButton onClick={handleCopyQuestion}>
                <ContentCopy
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          )}

          {editable && (
            <Tooltip title={"Delete"}>
              <IconButton
                onClick={handleDeleteQuestionPopup}
                disabled={formSubmitting}
                color="error"
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {editable && showDeleteModal && (
          <DeleteConfirmModal
            title={
              ["title", "subtitle", "checkbox"].includes(block.type)
                ? block?.label
                : ConsentLabelMap?.[block.type]
            }
            onOk={handleDeleteQuestion}
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default memo(ConsentItemWrapper);
